import React, { useState, useEffect, useMemo } from 'react';
import api from './api';
import Spinner from './Spinner';
import HamburgerMenu from './HamburgerMenu';

const PartsOnHandPage = ({ theme, toggleTheme }) => {
    const [sites, setSites] = useState([]);
    const [selectedSite, setSelectedSite] = useState('');
    const [parts, setParts] = useState([]);
    const [loading, setLoading] = useState(false);

    const [columnFilters, setColumnFilters] = useState({
        wc_cd: '', serno: '', mcn: '', wuc: '', sys_rsn: '', rqn_no: '', sup_sts: '', rcvd_dt: ''
    });

    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

    const isDark = theme === 'dark';
    const styles = {
        bg: isDark ? '#121212' : '#f4f7f6',
        card: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#333',
        border: isDark ? '#444' : '#ddd',
        input: isDark ? '#333' : '#fff',
        filterInput: {
            width: '100%', padding: '6px', borderRadius: '4px', border: `1px solid ${isDark ? '#555' : '#ccc'}`,
            backgroundColor: isDark ? '#333' : '#fff', color: isDark ? '#e0e0e0' : '#333', fontSize: '12px', boxSizing: 'border-box'
        },
        badge: {
            backgroundColor: isDark ? '#333' : '#e0e0e0', padding: '6px 12px', borderRadius: '20px',
            fontSize: '14px', fontWeight: '500', display: 'inline-block'
        },
        button: {
            backgroundColor: '#4CAF50',
            color: '#fff', border: 'none', padding: '10px 20px',
            borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }
    };

    useEffect(() => {
        api.get('/sites/').then(res => setSites(res.data.results || res.data)).catch(console.error);
    }, []);

    useEffect(() => {
        setLoading(true);
        const params = selectedSite ? { site_id: selectedSite } : {};
        api.get('/parts-on-hand/', { params })
            .then(res => setParts(res.data.results || res.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [selectedSite]);

    const handleColumnFilterChange = (column, value) => {
        setColumnFilters(prev => ({ ...prev, [column]: value }));
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    // --- Helper function to determine location based on Work Center ---
    const getLocation = (workCenter) => {
        const wc = String(workCenter).trim();
        if (wc === '100') return 'Hangar 1424 Room N114';
        if (wc === '200') return 'Hangar 1424 Room S101C';
        if (wc === '13B') return 'Bldg 2997 Room 102';
        if (wc === '140') return 'Hangar 2941 Room 116';
        if (wc === '13A') return 'Bldg 3154';
        if (wc === '500') return 'Hangar 2941 Sheet Metal Cage';
        if (wc === '520') return 'Hangar 2941 Sheet Metal Cage';
        return '—';
    };

    const filteredParts = useMemo(() => {
        return parts.filter(p => {
            for (const key in columnFilters) {
                if (columnFilters[key]) {
                    const cellValue = p[key] ? String(p[key]).toLowerCase() : '';

                    const searchTerms = columnFilters[key]
                        .toLowerCase()
                        .split(',')
                        .map(term => term.trim())
                        .filter(term => term !== '');

                    if (searchTerms.length > 0) {
                        const matchesAny = searchTerms.some(term => cellValue.includes(term));
                        if (!matchesAny) {
                            return false;
                        }
                    }
                }
            }
            return true;
        });
    }, [parts, columnFilters]);

    // Data used for the CSV Export (Contains EVERYTHING)
    const sortedParts = useMemo(() => {
        let sortableItems = [...filteredParts];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                const aValue = a[sortConfig.key] ? String(a[sortConfig.key]).toLowerCase() : '';
                const bValue = b[sortConfig.key] ? String(b[sortConfig.key]).toLowerCase() : '';
                if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [filteredParts, sortConfig]);

    // --- NEW: Data used for the UI Table (Hides specific Work Centers) ---
    const displayedParts = useMemo(() => {
        const hiddenWorkCenters = ['12C', '020', '021', '030', '040', '300'];
        return sortedParts.filter(part => {
            const wc = String(part.wc_cd || '').trim().toUpperCase();
            return !hiddenWorkCenters.includes(wc);
        });
    }, [sortedParts]);

    const exportToCSV = () => {
        if (sortedParts.length === 0) return;

        // Updated headers to include Location
        const headers = ['Site', 'Workcenter', 'Location', 'Buno', 'MCN', 'WUC', 'System Reason', 'RQN NO', 'Supply Status', 'Received Date'];
        const csvRows = [headers.join(',')];

        // This maps over sortedParts, so the hidden items STILL get exported!
        sortedParts.forEach(part => {
            const row = [
                part.site_name || '',
                part.wc_cd || '',
                getLocation(part.wc_cd),
                part.serno || '',
                part.mcn || '',
                part.wuc || '',
                part.sys_rsn || '',
                part.rqn_no || '',
                part.sup_sts || '',
                part.rcvd_dt || ''
            ].map(v => `"${String(v).replace(/"/g, '""')}"`);
            csvRows.push(row.join(','));
        });

        const csvData = new Blob([csvRows.join('\n')], { type: 'text/csv' });
        const csvUrl = URL.createObjectURL(csvData);
        const link = document.createElement('a');
        link.href = csvUrl;
        link.download = `Parts_On_Hand_Export_${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const SortableHeader = ({ label, sortKey }) => {
        const isSorted = sortConfig.key === sortKey;
        const arrow = isSorted ? (sortConfig.direction === 'ascending' ? ' ▲' : ' ▼') : ' ↕';
        return (
            <th
                style={{ padding: '12px', cursor: 'pointer', userSelect: 'none' }}
                onClick={() => requestSort(sortKey)}
                title={`Sort by ${label}`}
            >
                {label} <span style={{ fontSize: '10px', color: isDark ? '#aaa' : '#888' }}>{arrow}</span>
            </th>
        );
    };

    return (
        <div style={{ backgroundColor: styles.bg, minHeight: '100vh', padding: '20px', color: styles.text, fontFamily: 'sans-serif' }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>

                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', gap: '15px', flexWrap: 'wrap' }}>
                    <HamburgerMenu theme={theme} />
                    <h1 style={{ margin: 0 }}>Parts on Hand</h1>

                    {!loading && parts.length > 0 && (
                        <div style={styles.badge}>
                            Showing <strong>{displayedParts.length}</strong> {displayedParts.length === 1 ? 'part' : 'parts'}{' '}
                            <span style={{ fontSize: '12px', opacity: 0.8, fontWeight: 'normal' }}>
                                ({sortedParts.length} exporting)
                            </span>
                        </div>
                    )}

                    {!loading && parts.length > 0 && selectedSite && parts[0].uploaded_at && (
                        <div style={{ fontSize: '14px', color: isDark ? '#aaa' : '#666', fontStyle: 'italic' }}>
                            ⏳ Last updated: {new Date(parts[0].uploaded_at).toLocaleString()}
                        </div>
                    )}

                    <button
                        onClick={toggleTheme}
                        style={{
                            marginLeft: 'auto',
                            padding: '8px 16px',
                            borderRadius: '20px',
                            border: `1px solid ${styles.border}`,
                            backgroundColor: styles.card,
                            color: styles.text,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            fontWeight: 'bold',
                            fontSize: '14px'
                        }}
                    >
                        {isDark ? '☀️ Light Mode' : '🌙 Dark Mode'}
                    </button>
                </div>

                <div style={{ backgroundColor: styles.card, padding: '20px', borderRadius: '8px', border: `1px solid ${styles.border}`, marginBottom: '20px', display: 'flex', gap: '20px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '250px', maxWidth: '400px' }}>
                        <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>Filter by Site</label>
                        <select value={selectedSite} onChange={(e) => setSelectedSite(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '4px', border: `1px solid ${styles.border}`, background: styles.input, color: styles.text }}>
                            <option value="">All Sites</option>
                            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                    </div>

                    <div style={{ flex: 2, fontSize: '13px', color: isDark ? '#aaa' : '#666', paddingBottom: '10px' }}>
                        💡 <strong>Tip:</strong> Search for multiple items in any column by separating them with a comma (e.g. Workcenters <em>100, 200</em>)
                    </div>

                    <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                        <button style={styles.button} onClick={exportToCSV} disabled={sortedParts.length === 0}>
                            📥 Export to Excel (CSV)
                        </button>
                    </div>
                </div>

                {loading ? <Spinner /> : (
                    <div style={{ backgroundColor: styles.card, borderRadius: '8px', border: `1px solid ${styles.border}`, overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 280px)' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: styles.card, boxShadow: `0 2px 4px -2px ${styles.border}` }}>
                                <tr style={{ borderBottom: `1px solid ${styles.border}`, backgroundColor: isDark ? '#2a2a2a' : '#eaeaea', whiteSpace: 'nowrap' }}>
                                    <SortableHeader label="Site" sortKey="site_name" />
                                    <SortableHeader label="Workcenter" sortKey="wc_cd" />
                                    <th style={{ padding: '12px', userSelect: 'none' }}>Location</th>
                                    <SortableHeader label="Buno" sortKey="serno" />
                                    <SortableHeader label="MCN" sortKey="mcn" />
                                    <SortableHeader label="WUC" sortKey="wuc" />
                                    <SortableHeader label="System Reason" sortKey="sys_rsn" />
                                    <SortableHeader label="DDSN" sortKey="rqn_no" />
                                    <SortableHeader label="Supply Status" sortKey="sup_sts" />
                                    <SortableHeader label="Received Date" sortKey="rcvd_dt" />
                                </tr>
                                <tr style={{ borderBottom: `2px solid ${styles.border}`, backgroundColor: isDark ? '#1e1e1e' : '#f9f9f9' }}>
                                    <td style={{ padding: '8px' }}></td>
                                    <td style={{ padding: '8px' }}><input placeholder="Filter..." value={columnFilters.wc_cd} onChange={e => handleColumnFilterChange('wc_cd', e.target.value)} style={styles.filterInput} /></td>
                                    <td style={{ padding: '8px' }}><input disabled placeholder="" style={{...styles.filterInput, backgroundColor: isDark ? '#222' : '#eee', border: 'none'}} /></td>
                                    <td style={{ padding: '8px' }}><input placeholder="Filter..." value={columnFilters.serno} onChange={e => handleColumnFilterChange('serno', e.target.value)} style={styles.filterInput} /></td>
                                    <td style={{ padding: '8px' }}><input placeholder="Filter..." value={columnFilters.mcn} onChange={e => handleColumnFilterChange('mcn', e.target.value)} style={styles.filterInput} /></td>
                                    <td style={{ padding: '8px' }}><input placeholder="Filter..." value={columnFilters.wuc} onChange={e => handleColumnFilterChange('wuc', e.target.value)} style={styles.filterInput} /></td>
                                    <td style={{ padding: '8px' }}><input placeholder="Filter..." value={columnFilters.sys_rsn} onChange={e => handleColumnFilterChange('sys_rsn', e.target.value)} style={styles.filterInput} /></td>
                                    <td style={{ padding: '8px' }}><input placeholder="Filter..." value={columnFilters.rqn_no} onChange={e => handleColumnFilterChange('rqn_no', e.target.value)} style={styles.filterInput} /></td>
                                    <td style={{ padding: '8px' }}><input placeholder="Filter..." value={columnFilters.sup_sts} onChange={e => handleColumnFilterChange('sup_sts', e.target.value)} style={styles.filterInput} /></td>
                                    <td style={{ padding: '8px' }}><input placeholder="Filter..." value={columnFilters.rcvd_dt} onChange={e => handleColumnFilterChange('rcvd_dt', e.target.value)} style={styles.filterInput} /></td>
                                </tr>
                            </thead>
                            <tbody>

                                {displayedParts.length > 0 ? displayedParts.map(part => (
                                    <tr key={part.id} style={{ borderBottom: `1px solid ${styles.border}` }}>
                                        <td style={{ padding: '12px', whiteSpace: 'nowrap' }}>{part.site_name}</td>
                                        <td style={{ padding: '12px' }}>{part.wc_cd || '-'}</td>
                                        <td style={{ padding: '12px', color: isDark ? '#90caf9' : '#0277bd', fontWeight: '500' }}>{getLocation(part.wc_cd)}</td>
                                        <td style={{ padding: '12px', fontWeight: 'bold' }}>{part.serno || '-'}</td>
                                        <td style={{ padding: '12px' }}>{part.mcn || '-'}</td>
                                        <td style={{ padding: '12px' }}>{part.wuc || '-'}</td>
                                        <td style={{ padding: '12px' }}>{part.sys_rsn || '-'}</td>
                                        <td style={{ padding: '12px' }}>{part.rqn_no || '-'}</td>
                                        <td style={{ padding: '12px' }}>{part.sup_sts || '-'}</td>
                                        <td style={{ padding: '12px', whiteSpace: 'nowrap' }}>{part.rcvd_dt || '-'}</td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="10" style={{ padding: '20px', textAlign: 'center' }}>No parts found matching your filters.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PartsOnHandPage;
